import {
  Check,
  Close,
  EditOutlined,
  Info,
  LocalShippingOutlined,
  LocationOn,
  SettingsOutlined,
} from "@mui/icons-material";
import { Box, Button, Divider, FormControl, FormHelperText, MenuItem, Switch, Typography, Alert } from "@mui/material";
import { AppSelect, InputBase, InputLabel } from "components/common/Input";
import useLocationInformationOptions from "hooks/locations/useLocationInformationOptions";
import usePickup from "hooks/pickups/usePickup";
import theme from "lib/theme";
import { formatDistanceToNow } from "date-fns";
import { FormEvent, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { Location } from "types/location";
import useLocationInformation from "hooks/locations/useLocationInformation";
import useSaveLocationInformation from "hooks/locations/useSaveLocationInformation";
import { useAlert } from "hooks/alerts/useAlert";
import { DEFAULT_LOCATION_INFORMATION, LocationInformation } from "types/locationInformation";
import CertificateOfInsuranceUpload from "./CertificateOfInsuranceUpload";
import {
  MISSING_CERTIFICATE_MESSAGE,
  coiStoredOnLocation,
  coiUploadApplies,
  missingRequiredCertificate,
} from "./coiRequirement";
import { useMissingRequirement } from "./requirementError";
import { hasMissingRequiredSiteInformation } from "./siteInformationRequirement";

const SITE_INFORMATION_FORM_ID = "site-information-form";

const VALIDATION_SCROLL_GAP = 16;

const scrollPastValidationTarget = () => {
  requestAnimationFrame(() => {
    const field = document.activeElement?.closest(".MuiFormControl-root");
    if (!field) return;

    const headerBottom = document.querySelector(".MuiAppBar-root")?.getBoundingClientRect().bottom ?? 0;
    const overlap = headerBottom + VALIDATION_SCROLL_GAP - field.getBoundingClientRect().top;
    if (overlap > 0) document.scrollingElement?.scrollBy({ top: -overlap });
  });
};

const reportSiteInformationValidity = () => {
  const form = document.getElementById(SITE_INFORMATION_FORM_ID);
  if (!(form instanceof HTMLFormElement)) {
    return false;
  }

  return form.reportValidity();
};

const twoColumnGridSx = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 3,
  [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" },
};

const summaryColumnSx = {
  display: "flex",
  flexDirection: "column",
  gap: 1.5,
};

interface SiteInformationFormProps {
  step: number;
  goToNextStep?: () => void;
  registerPendingSave?: (saveIfExpanded: () => Promise<boolean>) => void;
}

type SummaryItemType = "info" | "success" | "negative";

interface SummaryItem {
  type: SummaryItemType;
  content: ReactNode;
}

const formatLocationAddress = (location: Location) => {
  const addressLine = [location.address, location.address2].filter(Boolean).join(", ");
  const cityLine = [location.city, location.state].filter(Boolean).join(", ");

  if (addressLine && cityLine) {
    return `${addressLine}, ${cityLine}`;
  }

  return addressLine || cityLine || location.name;
};

const formatHoursShort = (hours: string) => {
  return hours.replaceAll("_", " ");
};

const formatDockSummary = (height: string | null, zone: string | null) => {
  const heightShort = height?.replaceAll("_", " ");
  const zoneShort = zone?.replaceAll("_", " ");

  if (heightShort && zoneShort) {
    return `${heightShort}, ${zoneShort}`;
  }

  return heightShort || zoneShort;
};

const extractForkliftNote = (notes: string) => {
  const match = notes.match(/available between ([^.]+)/i);
  if (!match) {
    return null;
  }

  return match[1].replace(" and ", "-") + " only";
};

const buildBuildingSummary = (siteInformation: LocationInformation): SummaryItem[] => {
  const items: SummaryItem[] = [];

  if (siteInformation.buildingAccessHoursOption) {
    items.push({
      type: "info",
      content: (
        <>
          Hours: <strong>{formatHoursShort(siteInformation.buildingAccessHoursOption)}</strong>
        </>
      ),
    });
  }

  if (siteInformation.coiRequiredOption?.startsWith("Yes")) {
    items.push({
      type: "success",
      content: <strong>COI on file</strong>,
    });
  } else if (siteInformation.coiRequiredOption === "No") {
    items.push({
      type: "negative",
      content: "No COI required",
    });
  }

  if (siteInformation.freightElevatorNotesOption?.startsWith("Yes")) {
    const reservationNote = siteInformation.freightElevatorNotesOption.includes("reservation required")
      ? " (reservation required)"
      : "";
    items.push({
      type: "success",
      content: (
        <>
          <strong>Freight elevator</strong>
          {reservationNote}
        </>
      ),
    });
  } else if (siteInformation.freightElevatorNotesOption === "No") {
    items.push({
      type: "negative",
      content: "No freight elevator",
    });
  }

  items.push({
    type: siteInformation.unionLaborRequired ? "success" : "negative",
    content: siteInformation.unionLaborRequired ? <strong>Union labor required</strong> : "Union labor not required",
  });

  items.push({
    type: siteInformation.buildingCartsAvailable ? "success" : "negative",
    content: siteInformation.buildingCartsAvailable ? (
      <strong>Building provides carts/dollies</strong>
    ) : (
      "Building doesn't provide carts/dollies"
    ),
  });

  return items;
};

const buildDockSummary = (siteInformation: LocationInformation): SummaryItem[] => {
  const items: SummaryItem[] = [];

  if (siteInformation.loadingDockHeightOption || siteInformation.loadingZoneOption) {
    items.push({
      type: "info",
      content: (
        <>
          Dock:{" "}
          <strong>
            {formatDockSummary(siteInformation.loadingDockHeightOption, siteInformation.loadingZoneOption)}
          </strong>
        </>
      ),
    });
  }

  if (siteInformation.dockDoorDimensions) {
    items.push({
      type: "info",
      content: (
        <>
          Door: <strong>{siteInformation.dockDoorDimensions}</strong>
        </>
      ),
    });
  }

  items.push({
    type: siteInformation.boxTruckAccess ? "success" : "negative",
    content: siteInformation.boxTruckAccess ? <strong>Box truck (26')</strong> : "No box truck (26') access",
  });

  items.push({
    type: siteInformation.semiTruckAccess ? "success" : "negative",
    content: siteInformation.semiTruckAccess ? <strong>Semi-truck (53') access</strong> : "No semi-truck (53')",
  });

  const forkliftNote = extractForkliftNote(siteInformation.dockTruckNotes ?? "");
  if (forkliftNote) {
    items.push({
      type: "info",
      content: (
        <>
          <strong>Forklift</strong> available {forkliftNote}
        </>
      ),
    });
  }

  return items;
};

function SummaryIcon({ type }: { type: SummaryItemType }) {
  if (type === "success") {
    return <Check sx={{ fontSize: 18, color: "#2E7D32", flexShrink: 0, mt: 0.25 }} />;
  }

  if (type === "negative") {
    return <Close sx={{ fontSize: 18, color: theme.palette.text.disabled, flexShrink: 0, mt: 0.25 }} />;
  }

  if (type === "info") {
    return <Info sx={{ fontSize: 18, color: theme.palette.primary.main, flexShrink: 0, mt: 0.25 }} />;
  }

  return null;
}

const SummaryColumn = ({ title, items }: { title: string; items: SummaryItem[] }) => {
  return (
    <Box sx={summaryColumnSx}>
      <Typography
        variant="caption"
        fontWeight={700}
        color="text.secondary"
        sx={{ letterSpacing: "0.08em", textTransform: "uppercase" }}
      >
        {title}
      </Typography>
      {items.map((item, index) => (
        <Box key={index} sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
          <SummaryIcon type={item.type} />
          <Typography variant="body2">{item.content}</Typography>
        </Box>
      ))}
    </Box>
  );
};

const SiteInformationSummary = ({ siteInformation }: { siteInformation: LocationInformation }) => {
  const buildingItems = useMemo(() => buildBuildingSummary(siteInformation), [siteInformation]);
  const dockItems = useMemo(() => buildDockSummary(siteInformation), [siteInformation]);

  return (
    <Box
      sx={{
        ...twoColumnGridSx,
        mx: { xs: -2, md: -4 },
        px: { xs: 2, md: 4 },
        py: 3,
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      <SummaryColumn title="Building" items={buildingItems} />
      <SummaryColumn title="Dock & Truck" items={dockItems} />
    </Box>
  );
};

const LocationActionBar = ({
  location,
  siteInformation,
  isExpanded,
  isSaving,
  onEdit,
}: {
  location: Location;
  siteInformation?: LocationInformation | null;
  isExpanded: boolean;
  isSaving?: boolean;
  onEdit?: (event: React.MouseEvent<HTMLButtonElement>) => void;
}) => {
  return (
    <>
      <Divider />
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          gap: 2,
          flexDirection: { xs: "column", sm: "row" },
          py: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 0 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              backgroundColor: theme.palette.lightBlue.main,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: theme.palette.primary.main,
              flexShrink: 0,
            }}
          >
            <LocationOn sx={{ fontSize: 22 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography fontWeight={700}>{formatLocationAddress(location)}</Typography>
            {siteInformation?.updatedAt && (
              <Typography variant="body2" color="text.secondary">
                Last updated {formatDistanceToNow(new Date(siteInformation.updatedAt), { addSuffix: true })} by{" "}
                {siteInformation.lastUpdatedByName}
              </Typography>
            )}
          </Box>
        </Box>

        {isExpanded ? (
          <Button
            type="submit"
            form={SITE_INFORMATION_FORM_ID}
            variant="contained"
            color="primary"
            startIcon={<Close sx={{ fontSize: 18 }} />}
            disabled={isSaving}
            sx={{
              alignSelf: { xs: "stretch", sm: "center" },
              flexShrink: 0,
              fontWeight: 700,
              letterSpacing: "0.04em",
            }}
          >
            Done
          </Button>
        ) : (
          <Button
            variant="outlined"
            color="primary"
            startIcon={<EditOutlined sx={{ fontSize: 18 }} />}
            onClick={onEdit}
            sx={{
              alignSelf: { xs: "stretch", sm: "center" },
              flexShrink: 0,
              fontWeight: 700,
              letterSpacing: "0.04em",
            }}
          >
            Edit Details
          </Button>
        )}
      </Box>
      <Divider />
    </>
  );
};

interface SectionHeaderProps {
  icon: ReactNode;
  title: string;
  description: string;
}

const SectionHeader = ({ icon, title, description }: SectionHeaderProps) => {
  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 1,
            backgroundColor: theme.palette.lightBlue.main,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: theme.palette.primary.main,
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Typography fontWeight={700} fontSize="18px">
          {title}
        </Typography>
      </Box>
      <Typography mt={0.5} variant="body2" color="text.secondary">
        {description}
      </Typography>
    </Box>
  );
};

interface ToggleFieldProps {
  label: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

const ToggleField = ({ label, description, checked, onChange }: ToggleFieldProps) => {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        py: 1,
      }}
    >
      <Box>
        <Typography fontWeight={600}>{label}</Typography>
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      </Box>
      <Switch checked={checked} color="primary" onChange={(event) => onChange(event.target.checked)} />
    </Box>
  );
};

interface SelectFieldProps {
  label: string;
  value: string | null;
  options: Record<string, string>;
  placeholder?: string;
  description?: string;
  onChange?: (value: string) => void;
}

const SelectField = ({ label, value, options, placeholder, description, onChange }: SelectFieldProps) => {
  const showError = useMissingRequirement("schedule-pickup-site-information") && !value;

  return (
    <FormControl error={showError} variant="standard" fullWidth>
      <InputLabel shrink>{label}</InputLabel>
      <AppSelect
        fullWidth
        required
        error={showError}
        value={value ?? ""}
        placeholder={placeholder || "Select an option"}
        onChange={(event) => onChange?.(event.target.value as string)}
        style={{ backgroundColor: "white" }}
      >
        {Object.entries(options).map(([key, optionLabel]) => (
          <MenuItem key={key} value={key}>
            {optionLabel}
          </MenuItem>
        ))}
      </AppSelect>
      {showError && <FormHelperText>Required</FormHelperText>}
      {description && (
        <Typography mt={0.5} variant="body2" color="text.secondary">
          {description}
        </Typography>
      )}
    </FormControl>
  );
};

interface TextFieldProps {
  label: string;
  value: string | null;
  placeholder?: string;
  onChange: (value: string) => void;
}

const TextField = ({ label, value, placeholder, onChange }: TextFieldProps) => {
  return (
    <FormControl variant="standard" fullWidth>
      <InputLabel shrink>{label}</InputLabel>
      <InputBase
        value={value}
        placeholder={placeholder || "Enter a value"}
        onChange={(event) => onChange(event.target.value)}
      />
    </FormControl>
  );
};

interface TextAreaFieldProps {
  label: string;
  value: string;
  placeholder: string;
  onChange: (value: string) => void;
}

const TextAreaField = ({ label, value, placeholder, onChange }: TextAreaFieldProps) => {
  return (
    <FormControl variant="standard" fullWidth sx={{ mt: 3 }}>
      <InputLabel shrink>{label}</InputLabel>
      <InputBase multiline value={value} placeholder={placeholder} onChange={(event) => onChange(event.target.value)} />
    </FormControl>
  );
};

const FreightElevatorBanner = () => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 3 }}>
    <Alert
      severity="info"
      icon={false}
      sx={{ backgroundColor: "rgba(0, 137, 189, 0.06)", border: "1px solid rgba(0, 137, 189, 0.2)" }}
    >
      <Typography variant="body2" color="rgb(1, 87, 155)" component="span" fontWeight={800}>
        Heads up:{" "}
      </Typography>
      <Typography variant="body2" color="rgb(1, 87, 155)" component="span">
        Your team is expected to make this reservation with your property management ahead of the pickup.
      </Typography>
    </Alert>
  </Box>
);

const ExpandedSiteInformationFields = ({
  siteInformation,
  updateField,
  selectOptions,
  onSubmit,
  certificateUpload,
}: {
  siteInformation: LocationInformation;
  updateField: <K extends keyof LocationInformation>(field: K, fieldValue: LocationInformation[K]) => void;
  selectOptions: ReturnType<typeof useLocationInformationOptions>["selectOptions"];
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  certificateUpload?: ReactNode;
}) => {
  const [showFreightElevatorBanner, setShowFreightElevatorBanner] = useState(
    siteInformation.freightElevatorNotesOption === "reservation_required"
  );
  const [showProtectionRequirementsSection, setShowProtectionRequirementsSection] = useState(
    !!siteInformation.buildingProtectionNotes
  );
  const [showSpecialSecuritySection, setShowSpecialSecuritySection] = useState(!!siteInformation.specialAccessNotes);
  const formRef = useRef<HTMLFormElement | null>(null);

  useEffect(() => {
    const form = formRef.current;
    if (!form) return undefined;

    form.addEventListener("invalid", scrollPastValidationTarget, true);
    return () => form.removeEventListener("invalid", scrollPastValidationTarget, true);
  }, []);

  return (
    <Box
      component="form"
      id={SITE_INFORMATION_FORM_ID}
      ref={formRef}
      onSubmit={onSubmit}
      sx={{ backgroundColor: "#FAFBFD", padding: "25px" }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
        <SectionHeader
          icon={<SettingsOutlined sx={{ fontSize: 18 }} />}
          title="Building Requirements"
          description="Access, hours, and on-site rules the crew needs to know."
        />

        <Box sx={twoColumnGridSx} mt={2}>
          <SelectField
            label="Building access hours"
            value={siteInformation.buildingAccessHoursOption}
            options={selectOptions?.buildingAccessHoursOptions ?? {}}
            onChange={(nextValue) => updateField("buildingAccessHoursOption", nextValue)}
          />
          <SelectField
            label="COI (Certificate of Insurance) required?"
            value={siteInformation.coiRequiredOption}
            options={selectOptions?.coiRequiredOptions ?? {}}
            onChange={(nextValue) => updateField("coiRequiredOption", nextValue)}
          />
          <SelectField
            label="Freight elevator available?"
            value={siteInformation.freightElevatorNotesOption}
            options={selectOptions?.freightElevatorNotesOptions ?? {}}
            onChange={(nextValue) => {
              updateField("freightElevatorNotesOption", nextValue);
              setShowFreightElevatorBanner(nextValue === "reservation_required");
            }}
          />
          <SelectField
            label="Building protection needed?"
            value={showProtectionRequirementsSection ? "yes" : "no"}
            options={{
              no: "No",
              yes: "Yes - describe below",
            }}
            onChange={(nextValue) => {
              setShowProtectionRequirementsSection(nextValue === "yes");
              if (nextValue === "no") {
                updateField("buildingProtectionNotes", null);
              }
            }}
          />
        </Box>

        {certificateUpload}

        {showFreightElevatorBanner && <FreightElevatorBanner />}

        {showProtectionRequirementsSection && (
          <TextAreaField
            label="Describe protection requirements"
            value={siteInformation.buildingProtectionNotes ?? ""}
            placeholder="e.g. floor protection sheets, elevator pad padding, marble floor coverings, etc."
            onChange={(nextValue) => updateField("buildingProtectionNotes", nextValue)}
          />
        )}

        <Box sx={twoColumnGridSx} pt={2}>
          <SelectField
            label="Special security or check-in requirements?"
            value={showSpecialSecuritySection ? "yes" : "no"}
            options={{
              no: "No",
              yes: "Yes - describe below",
            }}
            onChange={(nextValue) => {
              setShowSpecialSecuritySection(nextValue === "yes");
              if (nextValue === "no") {
                updateField("specialAccessNotes", null);
              }
            }}
            description="e.g. visitor badges, crew names submitted in advance, lobby security desk check-in."
          />
        </Box>

        {showSpecialSecuritySection && (
          <TextAreaField
            label="Describe security/check-in process"
            value={siteInformation.specialAccessNotes ?? ""}
            placeholder="e.g. each crew member needs a visitor badge from the security desk. Submit names 24h in advance to building management."
            onChange={(nextValue) => updateField("specialAccessNotes", nextValue)}
          />
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 3 }}>
          <ToggleField
            label="Union labor required"
            description="Required for crews entering union-controlled buildings."
            checked={siteInformation.unionLaborRequired}
            onChange={(checked) => updateField("unionLaborRequired", checked)}
          />
          <Divider />
          <ToggleField
            label="Passenger elevator available"
            description="Used if no freight elevator is accessible."
            checked={siteInformation.passengerElevatorAvailable}
            onChange={(checked) => updateField("passengerElevatorAvailable", checked)}
          />
          <Divider />
          <ToggleField
            label="Building provides carts / dollies"
            description="If off, our crew brings their own."
            checked={siteInformation.buildingCartsAvailable}
            onChange={(checked) => updateField("buildingCartsAvailable", checked)}
          />
        </Box>
      </Box>

      <Divider sx={{ mt: 5, mb: 3 }} />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <SectionHeader
          icon={<LocalShippingOutlined sx={{ fontSize: 18 }} />}
          title="Dock & Truck Requirements"
          description="What the truck can do once it arrives."
        />

        <Box sx={twoColumnGridSx}>
          <SelectField
            label="Loading dock height"
            value={siteInformation.loadingDockHeightOption}
            options={selectOptions?.loadingDockHeightOptions ?? {}}
            onChange={(nextValue) => updateField("loadingDockHeightOption", nextValue)}
          />
          <SelectField
            label="Loading zone available?"
            value={siteInformation.loadingZoneOption}
            options={selectOptions?.loadingZoneOptions ?? {}}
            onChange={(nextValue) => updateField("loadingZoneOption", nextValue)}
          />
          <SelectField
            label="Time limit at loading zone"
            value={siteInformation.timeLimitLoadingZoneMinutes?.toString() ?? null}
            options={selectOptions?.timeLimitLoadingZoneMinutesOptions ?? {}}
            onChange={(nextValue) => updateField("timeLimitLoadingZoneMinutes", parseInt(nextValue))}
          />
          <TextField
            label="Dock door dimensions (W x H)"
            value={siteInformation.dockDoorDimensions ?? ""}
            placeholder="e.g. 9 ft x 10 ft"
            onChange={(nextValue) => updateField("dockDoorDimensions", nextValue)}
          />
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <ToggleField
            label="Semi-truck (53') access"
            description="Required for the largest pickups."
            checked={siteInformation.semiTruckAccess}
            onChange={(checked) => updateField("semiTruckAccess", checked)}
          />
          <Divider />
          <ToggleField
            label="Box truck (26') access"
            description="Standard for most pickups."
            checked={siteInformation.boxTruckAccess}
            onChange={(checked) => updateField("boxTruckAccess", checked)}
          />

          <TextAreaField
            label="Dock / truck notes"
            value={siteInformation.dockTruckNotes ?? ""}
            placeholder="Enter any additional notes about the dock or truck access."
            onChange={(nextValue) => updateField("dockTruckNotes", nextValue)}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default function SiteInformationForm({ step, goToNextStep, registerPendingSave }: SiteInformationFormProps) {
  const { pickup, createCertificateOfInsurance, deleteCertificateOfInsurance } = usePickup();
  const { alertError } = useAlert();
  const { selectOptions } = useLocationInformationOptions();
  const { saveLocationInformation, isSaving } = useSaveLocationInformation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [siteInformation, setSiteInformation] = useState<LocationInformation>(DEFAULT_LOCATION_INFORMATION);
  const {
    locationInformation,
    isLoading: isLocationInformationLoading,
    refetch,
    deleteCertificateOfInsurance: deleteLocationCertificate,
  } = useLocationInformation(pickup?.location?.uuid);

  const updateField = useCallback(
    <K extends keyof LocationInformation>(field: K, fieldValue: LocationInformation[K]) => {
      setSiteInformation((current) => ({ ...current, [field]: fieldValue }));
    },
    []
  );

  const persistSiteInformation = useCallback(
    async ({ collapse = true }: { collapse?: boolean } = {}) => {
      if (!pickup?.location?.uuid) {
        return false;
      }

      const savedLocationInformation = await saveLocationInformation({
        locationUuid: pickup.location.uuid,
        locationInformation: siteInformation,
        exists: !!locationInformation,
      });

      setSiteInformation(savedLocationInformation);
      await refetch();
      if (collapse) setIsExpanded(false);
      return true;
    },
    [locationInformation, pickup?.location?.uuid, refetch, saveLocationInformation, siteInformation]
  );

  const certificateIsMissing = missingRequiredCertificate({
    option: siteInformation.coiRequiredOption,
    locationFile: siteInformation.certificateOfInsuranceFile,
    pickupFile: pickup?.certificateOfInsuranceFile,
  });

  const rejectMissingCertificate = useCallback(() => {
    if (!certificateIsMissing) return false;

    alertError(MISSING_CERTIFICATE_MESSAGE);
    requestAnimationFrame(() => {
      document.getElementById("certificate-of-insurance")?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
    return true;
  }, [alertError, certificateIsMissing]);

  const persistCertificate = useCallback(
    async (signedId: string) => {
      if (!pickup?.location?.uuid) {
        alertError("Select a location before uploading a certificate of insurance.");
        return;
      }

      try {
        if (coiStoredOnLocation(siteInformation.coiRequiredOption)) {
          const saved = await saveLocationInformation({
            locationUuid: pickup.location.uuid,
            locationInformation: { ...siteInformation, certificateOfInsuranceFile: signedId },
            exists: !!locationInformation,
          });
          setSiteInformation(saved);
        } else {
          await saveLocationInformation({
            locationUuid: pickup.location.uuid,
            locationInformation: siteInformation,
            exists: !!locationInformation,
          });
          await createCertificateOfInsurance(signedId);
        }
        await refetch();
      } catch (error) {
        if (!(error instanceof Error && error.message === "Failed to save location information")) {
          alertError("There was an error saving the certificate of insurance. Please try again.");
        }
      }
    },
    [
      alertError,
      createCertificateOfInsurance,
      locationInformation,
      pickup?.location?.uuid,
      refetch,
      saveLocationInformation,
      siteInformation,
    ]
  );

  const removeCertificate = useCallback(async () => {
    if (!pickup?.location?.uuid) return;

    try {
      if (coiStoredOnLocation(siteInformation.coiRequiredOption)) {
        await deleteLocationCertificate();
      } else {
        await deleteCertificateOfInsurance();
      }
    } catch {
      alertError("There was an error removing the certificate of insurance. Please try again.");
    }
  }, [
    alertError,
    deleteCertificateOfInsurance,
    deleteLocationCertificate,
    pickup?.location?.uuid,
    siteInformation.coiRequiredOption,
  ]);

  const certificateUpload = coiUploadApplies(siteInformation.coiRequiredOption) ? (
    <CertificateOfInsuranceUpload
      option={siteInformation.coiRequiredOption}
      locationFile={siteInformation.certificateOfInsuranceFile}
      pickupFile={pickup?.certificateOfInsuranceFile}
      onUpload={persistCertificate}
      onDelete={removeCertificate}
    />
  ) : null;

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      try {
        const saved = await persistSiteInformation();
        if (saved) {
          goToNextStep?.();
        }
      } catch {
        // Error alert handled in save hook.
      }
    },
    [goToNextStep, persistSiteInformation]
  );

  const saveIfExpanded = useCallback(async () => {
    if (!pickup?.location?.uuid) {
      return true;
    }

    if (isLocationInformationLoading) {
      return false;
    }

    if (!isExpanded && !hasMissingRequiredSiteInformation(siteInformation)) {
      return !rejectMissingCertificate();
    }

    if (!isExpanded) {
      flushSync(() => {
        setIsExpanded(true);
      });
    }

    if (!reportSiteInformationValidity()) {
      return false;
    }

    try {
      const saved = await persistSiteInformation({ collapse: false });
      if (!saved || rejectMissingCertificate()) {
        setIsExpanded(true);
        return false;
      }

      setIsExpanded(false);
      return true;
    } catch {
      return false;
    }
  }, [
    isExpanded,
    isLocationInformationLoading,
    persistSiteInformation,
    pickup?.location?.uuid,
    rejectMissingCertificate,
    siteInformation,
  ]);

  registerPendingSave?.(saveIfExpanded);

  useEffect(() => {
    if (isLocationInformationLoading) {
      return;
    }

    if (locationInformation) {
      setSiteInformation(locationInformation);
      setIsExpanded(hasMissingRequiredSiteInformation(locationInformation));
    } else {
      setSiteInformation(DEFAULT_LOCATION_INFORMATION);
      setIsExpanded(true);
    }
  }, [locationInformation, isLocationInformationLoading]);

  return (
    <Box id="schedule-pickup-site-information" sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
      <Box>
        <Typography variant="h6" fontSize="18px" fontWeight="bold">
          {step}. Site Information
        </Typography>
        <Typography mt={0.5} variant="body2" color="text.secondary">
          Saved details about this location. Update if anything has changed since your last pickup.
        </Typography>

        {pickup?.location && (
          <Box mt={3}>
            <LocationActionBar
              location={pickup.location}
              siteInformation={locationInformation}
              isExpanded={isExpanded}
              isSaving={isSaving}
              onEdit={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setIsExpanded(true);
              }}
            />
          </Box>
        )}
      </Box>

      {pickup?.location && !isExpanded && <SiteInformationSummary siteInformation={siteInformation} />}
      {pickup?.location && !isExpanded && certificateUpload}

      {pickup?.location && isExpanded && (
        <ExpandedSiteInformationFields
          siteInformation={siteInformation}
          updateField={updateField}
          selectOptions={selectOptions}
          onSubmit={handleSubmit}
          certificateUpload={certificateUpload}
        />
      )}
    </Box>
  );
}
